import React, { memo, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid-pro'
import { useDispatch, useSelector } from 'react-redux'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { CommonModal } from 'src/components/shared/CommonModal'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { AdminFetchUsBankEarningOrders } from 'src/constants/actionTypes'
import {
  achTypeLabels,
  earningOrderStatusLabels,
  usBankDestinationTypeLabels,
} from 'src/constants/payments-ops/labels'
import type {
  AchType,
  EarningOrderStatus,
  UsBankDestinationType,
} from 'src/constants/payments-ops/enums'
import type { UsBankEarning, UsBankEarningOrder } from 'src/interfaces/usBankTransactions'
import {
  getUsBankEarningOrders,
  getUsBankEarningOrdersEarningId,
  getUsBankEarningOrdersError,
  getUsBankEarningOrdersLoading,
} from 'src/selectors/usBankTransactions'
import { addS } from 'src/utils/baseUtils'
import { formatDateOnly, formatDateTime, parseIsoDate, parseUnixTimestamp } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { summarizeEarningOrders } from 'src/utils/paymentsOps/usBankEarnings'

interface Props {
  earning: UsBankEarning | null
  onClose(): void
}

const ORDER_COLUMNS: Array<GridColDef<UsBankEarningOrder>> = [
  { field: 'orderId', headerName: 'Order ID', flex: 1, minWidth: 160 },
  {
    field: 'orderStatus',
    headerName: 'Status',
    width: 140,
    valueGetter: (value: EarningOrderStatus) => {
      return earningOrderStatusLabels[value]?.label ?? value
    },
  },
  {
    field: 'orderAmount',
    headerName: 'Net amount',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value: number | undefined) => {
      return formatCentsAsUsd(value)
    },
  },
  {
    field: 'orderExpectedSettlementDate',
    headerName: 'Expected settlement',
    width: 160,
    valueGetter: parseIsoDate,
    valueFormatter: formatDateOnly,
  },
  {
    field: 'destinationType',
    headerName: 'Destination',
    width: 130,
    valueGetter: (value: UsBankDestinationType | undefined) => {
      return value ? usBankDestinationTypeLabels[value] : '—'
    },
  },
  {
    field: 'orderAchType',
    headerName: 'ACH type',
    width: 110,
    valueGetter: (value: AchType | undefined) => {
      return value ? achTypeLabels[value] : '—'
    },
  },
  { field: 'usBankTransactionId', headerName: 'US Bank tx ID', width: 160 },
  {
    field: 'createdAt',
    headerName: 'Created at',
    width: 180,
    valueGetter: parseUnixTimestamp,
    valueFormatter: formatDateTime,
  },
]

const getRowId = (row: UsBankEarningOrder): string => {
  return row.orderId
}

const renderEmpty = () => {
  return <EmptyTableMessage message="No orders for this earning." />
}

const EarningOrdersModalComponent = ({ earning, onClose }: Props) => {
  const dispatch = useDispatch()
  const orders = useSelector(getUsBankEarningOrders)
  const ordersEarningId = useSelector(getUsBankEarningOrdersEarningId)
  const isLoading = useSelector(getUsBankEarningOrdersLoading)
  const error = useSelector(getUsBankEarningOrdersError)

  const earningId = earning?.earningId ?? null

  useEffect(() => {
    if (!earningId) {
      return
    }

    dispatch(AdminFetchUsBankEarningOrders.request({ earningId }))
  }, [dispatch, earningId])

  // The slice keeps the last earning's orders until the new request lands, so anything stored
  // for another earning (rows or error) is hidden rather than shown under this earning's title.
  const isCurrentEarning = ordersEarningId === earningId

  const earningOrders = useMemo(() => {
    return isCurrentEarning ? orders : []
  }, [isCurrentEarning, orders])

  const summary = useMemo(() => {
    return summarizeEarningOrders(earningOrders)
  }, [earningOrders])

  if (!earning) {
    return null
  }

  const visibleError = isCurrentEarning ? error : null

  return (
    <CommonModal
      isOpen
      title={`Orders for ${earning.earningId}`}
      className={tw`max-w-[960px]`}
      onClose={onClose}
    >
      <div css={tw`flex flex-col gap-3`}>
        <Typography variant="body2">
          {summary.count} {addS(summary.count, 'order')} · {formatCentsAsUsd(summary.netTotalCents)}{' '}
          net · {summary.completedCount} completed
        </Typography>
        {visibleError ? (
          <Typography variant="body2" css={tw`text-critical-500`}>
            {visibleError}
          </Typography>
        ) : (
          <GridTable<UsBankEarningOrder>
            rows={earningOrders}
            columns={ORDER_COLUMNS}
            getRowId={getRowId}
            isLoading={isLoading || !isCurrentEarning}
            autoHeight
            isStandalone
            sortingMode="client"
            hideFooter
            EmptyElement={renderEmpty}
            tableDataTestId="us-bank-orders-table"
          />
        )}
      </div>
    </CommonModal>
  )
}

export const EarningOrdersModal = memo(EarningOrdersModalComponent)
